import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { ActionSheetController } from '@ionic/angular';

import { LoginService } from 'src/app/services/login/login.service';
import { UserService } from 'src/app/services/user/user.service';
import { environment } from 'src/environments/environment';
import { MenuPermissions } from 'src/app/models/permissions/menu-permissions';
import { NavigationState } from 'src/app/models/navigation/navigation-state';
import { lastUrlAccesible, navigate } from 'src/app/reducers/navigation/navigation.actions';
import { PermissionService } from 'src/app/services/user/permission.service';
import { CapacityControl } from 'src/app/models/permissions/capacityControl';
import { CalendarService } from 'src/app/services/calendar/calendar.service';
import { Center } from 'src/app/models/center/center';
import { MembersControl } from 'src/app/models/permissions/membersControl';
import { DeviceService } from 'src/app/services/device/device.service';
import { DeviceController } from '@models/device/device-controller';
import { TranslateService } from '@ngx-translate/core';
import { OpenAccessControl } from '@models/device/open-access-control';
import { AccessTypeSecurity } from 'src/app/enumerates/device/access-type-security';
import { SettingService } from 'src/app/services/settings/settings.service';
import { NotificationAlertService } from '@services/index';
import { NotificationType } from 'src/app/enumerates/notification/notification-type';

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit {
    href = '';
    showMenu = false;
    showMoreOptionsMenu = false;
    clickBackButton = false;
    canAccessBackUrl = false;
    currentUrl = '';
    currentUrlData = '';
    tgManagerUrl = '';
    urlHistory: string[] = [];
    centersList: Center[] = [];
    displayedItems: MenuPermissions = {
        kpis: true,
        workoutGroup: false,
        assistance: false,
        schedules: false,
        capacityControl: {
            displayMenuOption: false,
            accessManual: false,
            accessControl: false,
            accessSettings: false
        },
        membersControl: {
            displayMembers: false,
            membersActive: false,
            membersCanceled: false,
            membersPotential: false
        }
    };
    navigation$: Observable<NavigationState>;
    showSupportButton = false;
    showCapacitySubMenu = false;
    showMembers = false;
    showPaymentSubMenu = false;
    devices: DeviceController[] = [];
    deviceName = '';
    showSuccessModal = false;
    showErrorModal = false;

    constructor(private readonly router: Router,
                private readonly loginService: LoginService,
                private readonly userService: UserService,
                private readonly calendarService: CalendarService,
                private readonly permissionService: PermissionService,
                private readonly settingService: SettingService,
                private readonly store: Store<{ navigation: NavigationState }>,
                private readonly deviceService: DeviceService,
                private readonly translateService: TranslateService,
                private readonly actionSheetCtrl: ActionSheetController,
                private readonly notificationAlertService: NotificationAlertService) {
        this.navigation$ = this.store.select(data => data.navigation);
        deviceService.devices.subscribe(devices => this.devices = [...devices.filter(device => !device.isExternalDevice)]);
    }

    ngOnInit(): void {
        this.buildMenu();
        this.currentUrl = this.router.url;
        this.tgManagerUrl = environment.tgManagerV3;

        this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
                if (!this.clickBackButton && this.canAccessBackUrl) {
                    this.urlHistory.push(this.currentUrl);
                } else {
                    this.clickBackButton = false;
                    if (!this.canAccessBackUrl) {
                        this.store.dispatch(lastUrlAccesible());
                    }
                }
                this.currentUrl = event.url;
                this.showMenu = false;
            }
        });

        this.navigation$.subscribe(res => {
            this.canAccessBackUrl = res.canAccessToLastUrl;
            if (!this.canAccessBackUrl) {
                this.urlHistory.pop();
                if (!this.urlHistory.length) {
                    this.canAccessBackUrl = false;
                }
            }
            this.showSupportButton = res.showSuppotButton;
        });
        this.centersList = this.userService.getCenters();
        this.deviceService.getData();
    }

    goTo(url: string): void {
        this.store.dispatch(navigate({ url }));
    }

    buildMenu(): void {
        this.displayedItems = {
            kpis: this.permissionService.hasSubsectionBusinessKpi(),
            workoutGroup: this.permissionService.canAssignWorkoutsPermission(),
            assistance: this.permissionService.hasSubsectionCalendar(),
            capacityControl: this.checkDisplayCapacityControl(),
            membersControl: this.checkDisplayMembers(),
            schedules: this.permissionService.isActiveSubsectionFitness()
        };
    }

    talkToCustomerSupport(): void {
        if ((window as any).HubSpotConversations) {
            (window as any).HubSpotConversations.widget.load();
            (window as any).HubSpotConversations.widget.open();
        } else {
            console.error('HubSpotConversations no está disponible');
        }
    }

    checkDisplayCapacityControl(): CapacityControl {
        const staffPermissionFull = this.permissionService.getPermissions().staffPermissionFull;
        let capacityControl: CapacityControl = {
            displayMenuOption: false,
            accessManual: false,
            accessControl: false,
            accessSettings: false
        };

        if (staffPermissionFull?.staffPermission.subsectionAutomaticCapacity
        || staffPermissionFull?.staffPermission.subsectionCapacityStatus
        || staffPermissionFull?.staffPermission.subsectionStaffsAssistance) {
            capacityControl = {
                displayMenuOption: true,
                accessManual: staffPermissionFull?.staffPermission.subsectionStaffsAssistance || false,
                accessControl: staffPermissionFull?.staffPermission.subsectionAutomaticCapacity || false,
                accessSettings: staffPermissionFull?.staffPermission.subsectionCapacityStatus || false
            };
        }


        return capacityControl;
    }

    checkDisplayMembers():  MembersControl {
        const staffPermissionFull = this.permissionService.getPermissions().staffPermissionFull;
        let membersControl: MembersControl = {
            displayMembers: false,
            membersActive: false,
            membersCanceled: false,
            membersPotential: false
        };

        if ( staffPermissionFull?.staffPermission.subsectionMembersActive
        || staffPermissionFull?.staffPermission.subsectionMembersPotential
        || staffPermissionFull?.staffPermission.subsectionMembersCanceled) {
            membersControl = {
                displayMembers: true,
                membersActive: staffPermissionFull?.staffPermission.subsectionMembersActive || false,
                membersPotential: staffPermissionFull?.staffPermission.subsectionMembersPotential || false,
                membersCanceled: staffPermissionFull?.staffPermission.subsectionMembersCanceled || false
            };
        }

        return membersControl;
    }

    goBackUrl(): void {
        this.clickBackButton = true;
        const backUrl = this.urlHistory.pop();
        if (backUrl) {
            void this.router.navigateByUrl(backUrl);
        }
    }

    moreOptionMenu(): void {
        this.showMoreOptionsMenu = !this.showMoreOptionsMenu;
    }

    async updateCenter(): Promise<void> {
        if (this.centersList.length > 1 ) {
            const actionSheet = await this.actionSheetCtrl.create({
                header: this.translateService.instant('select_center'),
                buttons: this.centersList.map((center: Center) => ({ text: center.center.name, data: center }))
            });

            await actionSheet.present();
            const result = await actionSheet.onDidDismiss();

            if (result.role !== 'backdrop') {
                this.settingService.updateCenter(result.data);
                this.buildMenu();
                void this.router.navigate(['/schedule']).then(() => window.location.reload());
            }
        }
    }

    updateDesktopView(): void {
        window.open(this.tgManagerUrl, '_blank');
    }

    logout(): void {
        this.calendarService.resetDate();
        this.loginService.logout();
    }

    toggleMenu(): void {
        this.showMenu = !this.showMenu;
    }

    toggleShowCapacity(): void {
        this.showCapacitySubMenu = !this.showCapacitySubMenu;
    }

    toggleShowMembers(): void {
        this.showMembers = !this.showMembers;
    }

    toggleShowPayment(): void {
        this.showPaymentSubMenu = !this.showPaymentSubMenu;
    }

    async openAccessDevice(): Promise<void> {
        const actionSheet = await this.actionSheetCtrl.create({
            header: this.translateService.instant('select_access_device'),
            buttons: this.devices.map((device: DeviceController) => ({ text: device.name, data: device }))
        });

        await actionSheet.present();
        const result = await actionSheet.onDidDismiss();

        if (result.role !== 'backdrop') {
            this.tryOpenAccessStaff(result.data);
        }
    }

    onSuccessCloseModal(): void {
        this.showSuccessModal = false;
    }

    onErrorCloseModal(): void {
        this.showErrorModal = false;
    }

    private tryOpenAccessStaff(device: DeviceController): void {
        this.deviceName = device.name;

        const openAccessControl = this.buildOpenActionStaff(device);
        this.deviceService.manualAccess(openAccessControl)
            .subscribe({
                next: () => {
                    void this.notificationAlertService.add({
                        type: NotificationType.success,
                        class: 'success-notification',
                        description: this.translateService.instant('authorized_opening_of', { device: this.deviceName }),
                        icon: '',
                        title: this.translateService.instant('access_device_opened')
                    });
                },
                error: () => {
                    void this.notificationAlertService.add({
                        type: NotificationType.error,
                        class: 'error-notification',
                        description: this.translateService.instant('retry_later'),
                        icon: '',
                        title: this.translateService.instant('an_error_has_ocurred')
                    });
                }});
    }

    private buildOpenActionStaff(device: DeviceController): OpenAccessControl {
        const openAction: OpenAccessControl = {
            deviceHashId: device.id,
            accessType: AccessTypeSecurity.entrance,
            idStaff: this.userService.getUser().staff.idStaff,
            authenticationType: 4,
            bypassRestrictions: true
        };

        return openAction;
    }
}
